
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pen, Check, X } from "lucide-react";
import { Slab } from "@/types/slab";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, stockStatusStyles } from "@/lib/utils";

interface ProductsTableProps {
  slabs: Slab[];
  isLoading: boolean;
  editingId: string | null;
  editValues: { price: number; quantity: number };
  onStartEdit: (slab: Slab) => void;
  onCancelEdit: () => void;
  onUpdateField: () => void;
  onEditValueChange: (values: { price: number; quantity: number }) => void;
}

export function ProductsTable({
  slabs,
  isLoading,
  editingId,
  editValues,
  onStartEdit,
  onCancelEdit,
  onUpdateField,
  onEditValueChange,
}: ProductsTableProps) {
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={10} className="text-center py-4">
          Carregando...
        </TableCell>
      </TableRow>
    );
  }

  if (slabs.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={10} className="text-center py-4">
          Nenhum produto encontrado
        </TableCell>
      </TableRow>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">Código</TableHead>
            <TableHead className="w-[200px]">Nome</TableHead>
            <TableHead className="w-[120px]">Tipo</TableHead>
            <TableHead className="w-[120px]">Localização</TableHead>
            <TableHead className="w-[120px]">Preço</TableHead>
            <TableHead className="w-[120px]">Dimensões</TableHead>
            <TableHead className="w-[80px]">SQFT</TableHead>
            <TableHead className="w-[100px]">Qtd</TableHead>
            <TableHead className="w-[120px]">Status</TableHead>
            <TableHead className="w-[80px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {slabs.map((slab) => (
            <TableRow key={slab.id}>
              <TableCell className="font-mono">{slab.code}</TableCell>
              <TableCell>{slab.name}</TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {slab.type}
                </Badge>
              </TableCell>
              <TableCell>{slab.location || '-'}</TableCell>
              <TableCell>
                {editingId === slab.id ? (
                  <input
                    type="number"
                    className="w-24 px-2 py-1 border rounded bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={editValues.price}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (!isNaN(value) && value >= 0) {
                        onEditValueChange({ ...editValues, price: value });
                      }
                    }}
                  />
                ) : (
                  <span className="font-medium">{formatCurrency(slab.price)}</span>
                )}
              </TableCell>
              <TableCell>
                {slab.dimensions ? (
                  `${slab.dimensions.length}x${slab.dimensions.height}`
                ) : '-'}
              </TableCell>
              <TableCell>{slab.sqft}</TableCell>
              <TableCell>
                {editingId === slab.id ? (
                  <input
                    type="number"
                    className="w-16 px-2 py-1 border rounded bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={editValues.quantity}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (!isNaN(value) && value >= 0) {
                        onEditValueChange({ ...editValues, quantity: value });
                      }
                    }}
                  />
                ) : (
                  <span className="font-medium">{slab.quantity}</span>
                )}
              </TableCell>
              <TableCell>
                {slab.quantity === 0 ? (
                  <Badge className={stockStatusStyles.outOfStock.badge}>
                    Out of stock
                  </Badge>
                ) : slab.quantity < 2 ? (
                  <Badge className={stockStatusStyles.lowStock.badge}>
                    Low stock
                  </Badge>
                ) : null}
              </TableCell>
              <TableCell>
                {editingId === slab.id ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onUpdateField}
                      className="h-8 w-8"
                    >
                      <Check className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onCancelEdit}
                      className="h-8 w-8"
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onStartEdit(slab)}
                    className="h-8 w-8"
                  >
                    <Pen className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
